import React, {useState, useEffect, useMemo, useRef} from 'react'
import {Box, Tooltip, Typography, useTheme} from '@mui/material'
import {MoreHoriz} from "@mui/icons-material"
import {getColorForScore} from "../../util/utils"
import {fmtPct} from "../../util/prepkit-utils"

const easeOut = (t) => 1 - Math.pow(1 - t, 3)

// the gradient ring on the score box (197)
const RING_WIDTH = 7

/*
 * How far either side of the score the box's two gradient stops are sampled from.
 *
 * The inline pill keeps the original proportional `score * 0.1`, which at a 95% score samples 0.855
 * and 1.0 -- two greens a few hex points apart on the getColorForScore ramp. As a solid pill that is
 * fine. As a 7px stroke and as text it reads as flat, so the box samples wider.
 *
 * ABSOLUTE rather than proportional, so the sweep does not collapse at the bottom of the range
 * either. getColorForScore's own 0.5 floor and 1.0 ceiling still clamp the ends, so a perfect score
 * simply spreads downward -- which is the only range it has.
 */
const BOX_GRADIENT_SPREAD = 0.18

/*
 * onAnimationEnd (076): optional, fired once when the count-up settles. TopicScore uses it to
 * time the confetti so the burst lands with the final number rather than before it. Optional
 * by design — the other call sites pass nothing and are unaffected.
 */
const Score = ({variant, topic, shadow = 2, size, animate = false, onAnimationEnd, ...props}) => {

	const theme = useTheme()
	const score = topic ? topic.percentCorrect : 0
	const [animatedScore, setAnimatedScore] = useState(0)
	// the narrow pair -- the inline pill's background, unchanged (197)
	const backgroundColor1 = getColorForScore(Math.min(score + (score * 0.1), 1))
	const backgroundColor2 = getColorForScore(Math.max(score - (score * 0.1), 0.5))

	/*
	 * The box's gradient (197). ONE gradient, used for both the ring and the digits so the two cannot
	 * disagree about colour or direction.
	 */
	const boxGradient = `linear-gradient(to bottom left, ${getColorForScore(Math.min(score + BOX_GRADIENT_SPREAD, 1))}, ${getColorForScore(Math.max(score - BOX_GRADIENT_SPREAD, 0.5))})`
	/*
	 * `contained` and `outlined` no longer differ (197): the box is one gradient revealed by its ring
	 * and its digits either way, so the paper fill and the solid text colour they used to select
	 * between are both gone. `variant` still picks the inline pill, which is unchanged.
	 */
	const inline = variant === "inline"
	const isComplete = useMemo(() => {
		return topic && topic.percentAnswered === 1
	}, [topic])

	// held in a ref so a re-render mid-animation can't fire the callback a second time
	const endedRef = useRef(false)
	const onEndRef = useRef(onAnimationEnd)
	onEndRef.current = onAnimationEnd

	useEffect(() => {
		endedRef.current = false

		const done = () => {
			if (endedRef.current) return
			endedRef.current = true
			if (onEndRef.current) onEndRef.current()
		}

		if (animate) {
			let start = 0
			const duration = 3500 // x seconds duration for the animation
			const startTime = performance.now()

			const animateScore = () => {
				const currentTime = performance.now()
				const elapsed = currentTime - startTime
				const progress = Math.min(elapsed / duration, 1) // Keep it between 0 and 1

				const easedProgress = easeOut(progress)
				setAnimatedScore(easedProgress * score)

				if (progress < 1) {
					requestAnimationFrame(animateScore)
				}
				else {
					done()
				}
			}

			requestAnimationFrame(animateScore)
		} else {
			setAnimatedScore(score)
			done()
		}
	}, [animate, score])

	if (isNaN(score)) return null

	if (inline) {

		// inline

		return <Tooltip
			title={<>
				You scored {fmtPct(score)}<br/>
				{topic.questionCount} questions<br/>
				{topic.correctCount} correct, {topic.incorrectCount} incorrect
			</>}
		>
			<Typography variant={"body2"}
			            sx={[
				            {
					            color: isComplete
						            ? `white`
						            : theme.palette.text.primary,
					            background: isComplete
						            ? `linear-gradient(to bottom left, ${backgroundColor1}, ${backgroundColor2})`
						            : topic.answeredCount > 0
							            ? `linear-gradient(to bottom left, ${theme.palette.background.default}, ${theme.palette.divider})`
							            : `transparent`,
					            border: isComplete
						            ? `none`
						            : `0px solid ${theme.palette.divider}`,
					            padding: '3px 7px 1px 9px',
					            borderRadius: '15px',
				            },
				            props.sx || {}
			            ]}
			>
				{
					isComplete
						? fmtPct(animatedScore)
						: 'In Progress'
				}
			</Typography>
		</Tooltip>
	}
	else {

		// contained or outlined

		if (isComplete === 0) return null

		return isComplete < 1
			? <Tooltip title={"In progress"} placement={"top"}>
				<Box
					border={`1px solid ${theme.palette.divider}`}
					borderRadius={1}
					px={1}
				>
					<MoreHoriz sx={{color: theme.palette.divider}}/>
				</Box>
			</Tooltip>
			: <Box
				display={"flex"}
				flexDirection={"column"}
				justifyContent={"center"}
				alignItems={"center"}
			>
				{/*
				  * The gradient is the INK, not the fill (197): one gradient layer revealed only by a
				  * 7px ring and by the glyphs of the number, with a genuinely transparent centre.
				  *
				  * The ring is a ::before rather than this box's own border because A MASK APPLIES TO
				  * DESCENDANTS. Masking this element would have clipped the number away with the fill --
				  * the digits sit in the padding box, which is exactly the region the mask excludes. As a
				  * pseudo-element the ring is the Typography's SIBLING, so the mask touches only the
				  * ring's own background.
				  *
				  * No boxShadow: a shadow under a transparent centre casts from a shape that is not
				  * there.
				  */}
				<Box
					sx={{
						position: 'relative',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						width: `${size * 2.25}px`,
						height: `${size * 2.25}px`,
						'&::before': {
							content: '""',
							position: 'absolute',
							inset: 0,
							borderRadius: `${size / 1.5}px`,
							border: `${RING_WIDTH}px solid transparent`,
							/*
							 * Two identical mask layers, one clipped to the padding box, composited to
							 * exclude one from the other -- what survives is the border band alone. This is
							 * the only way to get a gradient border while keeping the fill transparent; the
							 * usual trick lays an opaque box over a gradient, which needs a solid fill.
							 */
							background: `${boxGradient} border-box`,
							WebkitMask: 'linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0)',
							mask: 'linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0)',
							WebkitMaskComposite: 'xor',
							maskComposite: 'exclude',
							pointerEvents: 'none',
						},
					}}
				>
					{/*
					  * background-clip: text on the Typography, so the <sup>%</sup> -- an inline child with
					  * no colour of its own -- is clipped from the SAME gradient box as the digits rather
					  * than getting a sweep of its own. Number and percent read as one object.
					  */}
					<Typography sx={{
						background: boxGradient,
						WebkitBackgroundClip: 'text',
						backgroundClip: 'text',
						color: 'transparent',
						// Safari paints the glyphs over the clipped background unless the FILL is cleared too
						WebkitTextFillColor: 'transparent',
						fontSize: size || 'inherit',
						fontWeight: 500,
						letterSpacing: `-${size * .05}px`,
						pl: `${size * .15}px`,
						pt: `${size * .1}px`,
					}}>
						{Math.round(animatedScore * 100)}<sup style={{fontSize: ".5em"}}>%</sup>
					</Typography>
				</Box>
			</Box>
	}
}

export default Score
